import axios from "axios";
import localApiConfig from "./local-api-config.json";

// Canonical-domain guard: on icaard.com (or www.icaard.com) always call the API
// on the SAME origin the user is browsing — eliminates cross-domain CORS and
// redirect failures that surfaced as 503/Network errors in production.
const isLocalHost = typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname);
const isLocalDevelopment = process.env.NODE_ENV === "development" && isLocalHost;
// Local production bundles are served through serve_local_build.py, which
// proxies same-origin /api requests to the current local backend on port 8001.
// Do not bake the machine-specific REACT_APP_BACKEND_URL from .env.local into
// a production bundle opened at localhost (it can point at a stale port).
const envBackend = isLocalHost
  ? (localApiConfig.backendUrl || window.location.origin)
  : process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL =
  typeof window !== "undefined" && /(^|\.)icaard\.com$/i.test(window.location.hostname)
    ? window.location.origin
    : envBackend;
export const API = `${BACKEND_URL}/api`;
export const backendUrl = BACKEND_URL;

// Resolve media paths through the same canonical backend origin as API calls.
// This also repairs legacy localhost URLs already stored in profile records.
export const resolveMediaUrl = (value) => {
  const raw = (value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("/")) return `${backendUrl}${raw}`;
  if (!/^https?:\/\//i.test(raw) || typeof window === "undefined") return raw;
  try {
    const url = new URL(raw);
    const hostname = url.hostname.toLowerCase();
    const currentHost = window.location.hostname.toLowerCase();
    const isLoopback = (host) => host === "localhost" || host === "127.0.0.1";
    const isIcaard = (host) => host === "icaard.com" || host === "www.icaard.com";
    if (isLoopback(hostname) && !isLoopback(currentHost)) {
      return `${backendUrl}${url.pathname}${url.search}${url.hash}`;
    }
    if (isIcaard(hostname)) {
      return `${window.location.origin}${url.pathname}${url.search}${url.hash}`;
    }
    return raw;
  } catch (_) {
    return raw;
  }
};

export const api = axios.create({ baseURL: API, withCredentials: true });
if (isLocalDevelopment) api.interceptors.response.use(undefined, (error) => {
  console.warn("[local-api]", error.message, error.config?.baseURL, error.config?.url, error.response?.status);
  return Promise.reject(error);
});


api.interceptors.request.use((config) => {
  const token = localStorage.getItem("icard_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-retry — transient proxy / cold-start failures (network error, 502/503/504,
// Cloudflare 52x, HTML error pages) are silently retried for safe GET requests
// before the user ever sees a "connection hiccup".
const _isTransient = (e) => {
  if (!e?.response) return true;
  const s = e.response.status;
  const raw = e.response.data;
  if (s === 502 || s === 503 || s === 504 || s >= 520) return true;
  return typeof raw === "string" && raw.includes("<html");
};
const RETRY_DELAYS = [400, 1000, 2200];
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config || {};
  const method = (cfg.method || "get").toLowerCase();
  const online = typeof navigator === "undefined" || navigator.onLine !== false;
  if (method === "get" && online && _isTransient(error)) {
    cfg.__retries = (cfg.__retries || 0) + 1;
    if (cfg.__retries <= RETRY_DELAYS.length) {
      await new Promise((r) => setTimeout(r, RETRY_DELAYS[cfg.__retries - 1]));
      return api.request(cfg);
    }
  }
  return Promise.reject(error);
});

// Offline queue — safe non-payment writes made offline are stored and
// auto-replayed when connectivity returns (see lib/offlineQueue.js).
api.interceptors.response.use(undefined, (error) => {
  if (!error.response && typeof navigator !== "undefined" && !navigator.onLine) {
    const cfg = error.config || {};
    const path = (cfg.url || "").replace(API, "");
    // Lazy import to avoid a require cycle.
    const { isQueueable, enqueue } = require("./offlineQueue");
    if (isQueueable(path, cfg.method)) {
      let data = cfg.data;
      try { data = typeof data === "string" ? JSON.parse(data) : data; } catch (_) {}
      enqueue({ method: cfg.method, url: path, data });
      return Promise.resolve({ data: { ok: true, queued: true }, status: 202, queued: true });
    }
  }
  return Promise.reject(error);
});

export function formatError(e) {
  const status = e?.response?.status;
  const raw = e?.response?.data;
  // Cloudflare / proxy hiccups return HTML or 52x — never show that raw text.
  if ((typeof raw === "string" && (raw.includes("<html") || /cloudflare/i.test(raw))) || status >= 520) {
    return "Connection hiccup — please try again in a moment.";
  }
  const d = e?.response?.data?.detail;
  // Structured fraud-limit / window-expired errors carry a friendly message.
  if (d && typeof d === "object" && !Array.isArray(d)) {
    if (d.message) return d.message;
    if (d.friendly && d.friendly.length) return d.friendly[0];
    if (d.reasons && d.reasons.length) return d.reasons[0];
  }
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return e?.message || "Something went wrong";
}

export function fmt(n) {
  const v = Number(n || 0);
  return `GH₵ ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Pulls the iCaard serial (DCN) out of either a bare DCN string or a full
 * https://icaard.com/s/<DCN> URL. Keeps every in-app scanner working now that
 * the QR encodes a URL so 3rd-party phone cameras open the universal scan flow.
 */
export function extractSerial(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  const m = s.toUpperCase().match(/(ICD-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}|CUST[A-Z0-9]+|STU[A-Z0-9]+|MRC[A-Z0-9]+|ORG[A-Z0-9]+|STF[A-Z0-9]+|DRV[A-Z0-9]+|CDT[A-Z0-9]+|AGT[A-Z0-9]+|BIZ[A-Z0-9]+|INS[A-Z0-9]+|SPT[A-Z0-9]+)/);
  if (m) return m[0];
  // Last-resort: take the trailing path segment
  return s.split("/").pop().split("?")[0].trim().toUpperCase();
}

